// Plays raw 16-bit PCM as it streams in: each network chunk is scheduled the moment it lands,
// back to back on one Web Audio clock. Sound starts on the first bytes (an <audio> element
// buffers ~2s of MP3 first), and the next sentence queues seamlessly behind the current one.

/** Aura-2 is asked for linear16 at this rate (see the /api/morrow/voice/speak route). */
export const SPEECH_SAMPLE_RATE = 24000;

export function int16ToFloat(bytes: Uint8Array): Float32Array<ArrayBuffer> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const out = new Float32Array(bytes.byteLength >> 1);
  for (let i = 0; i < out.length; i += 1) out[i] = view.getInt16(i * 2, true) / 32768;
  return out;
}

export class PcmPlayer {
  private readonly ctx = new AudioContext();
  // Everything plays through this, so the speaking level can be read (for the voice orb).
  private readonly out: AnalyserNode;
  private readonly frame = new Float32Array(512);
  private next = 0;
  private readonly live = new Set<AudioBufferSourceNode>();

  constructor() {
    this.out = this.ctx.createAnalyser();
    this.out.fftSize = 512;
    this.out.connect(this.ctx.destination);
  }

  /** How loud it's speaking right now, 0–1. */
  level(): number {
    this.out.getFloatTimeDomainData(this.frame);
    let sum = 0;
    for (const v of this.frame) sum += v * v;
    return Math.min(1, Math.sqrt(sum / this.frame.length) * 4);
  }

  /** Something is playing or scheduled to play. */
  get busy(): boolean {
    return this.live.size > 0 || this.ctx.currentTime < this.next - 0.01;
  }

  /** Must be called from a user gesture once, or the browser keeps the context suspended. */
  resume(): void {
    void this.ctx.resume().catch(() => undefined);
  }

  /** Cut everything off now (barge-in / end). */
  stop(): void {
    for (const src of this.live) {
      try {
        src.stop();
      } catch {
        /* already stopped */
      }
    }
    this.live.clear();
    this.next = 0;
  }

  close(): void {
    this.stop();
    void this.ctx.close().catch(() => undefined);
  }

  /** Queue samples right after whatever is already scheduled. */
  play(samples: Float32Array<ArrayBuffer>): void {
    if (samples.length === 0) return;
    const buffer = this.ctx.createBuffer(1, samples.length, SPEECH_SAMPLE_RATE);
    buffer.copyToChannel(samples, 0);
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(this.out);
    const at = Math.max(this.ctx.currentTime + 0.03, this.next);
    src.start(at);
    this.next = at + buffer.duration;
    this.live.add(src);
    src.onended = () => this.live.delete(src);
  }

  /** Read a PCM response and schedule it chunk by chunk; returns once it's all scheduled. */
  async playStream(res: Response, stillWanted: () => boolean): Promise<void> {
    if (!res.body) return;
    const reader = res.body.getReader();
    let carry: number | null = null;
    for (;;) {
      const { value, done } = await reader.read();
      if (done) return;
      if (!stillWanted()) {
        void reader.cancel().catch(() => undefined);
        return;
      }
      let bytes = value;
      if (carry !== null) {
        const merged = new Uint8Array(bytes.length + 1);
        merged[0] = carry;
        merged.set(bytes, 1);
        bytes = merged;
        carry = null;
      }
      if (bytes.length % 2 === 1) {
        carry = bytes[bytes.length - 1]!;
        bytes = bytes.subarray(0, bytes.length - 1);
      }
      this.play(int16ToFloat(bytes));
    }
  }

  /** Read a whole PCM response into samples (for short clips we replay, like fillers). */
  static async samples(res: Response): Promise<Float32Array<ArrayBuffer>> {
    const bytes = new Uint8Array(await res.arrayBuffer());
    return int16ToFloat(bytes.length % 2 ? bytes.subarray(0, bytes.length - 1) : bytes);
  }

  /** Resolve once everything scheduled has played (or it's no longer wanted). */
  async idle(stillWanted: () => boolean): Promise<void> {
    while (stillWanted() && this.busy) {
      await new Promise((r) => setTimeout(r, 80));
    }
  }
}
